import { useMemo, useState } from "react";
import { GradientText, Title } from "lola-framework-ui-test";
import { useTranslation } from "react-i18next";
import { useStore } from "../../store/globalState";
import { FlagCircle } from "../FlagCircle";
import type { CountryCapability } from "../../utils/catalogCountries";
import { currencyForCountry } from "../../utils/catalogCountries";
import { countryDisplayName } from "../../utils/country-display";
import { ScrollFadeIndicator } from "../ScrollFadeIndicator";
import { CloseLight } from "../../assets/navIcons";


export interface SelectCountrySlotProps {
  titleKey: string;
  countries: CountryCapability[];
  selected?: string | null;
  onSelect: (countryCode: string) => void;
  onClose: () => void;
  /** i18n namespace; defaults to send. */
  ns?: string;
  /** Right-hand label. Defaults to the country's currency code. */
  trailingLabel?: (country: CountryCapability) => string | null;
  searchKey?: string;
  emptyKey?: string;
  cancelKey?: string;
}

export const SelectCountrySlot: React.FC<SelectCountrySlotProps> = ({
  titleKey,
  countries,
  selected,
  onSelect,
  onClose,
  ns = "send",
  trailingLabel,
  searchKey = "transfer.searchCountry",
  emptyKey = "transfer.noCountriesFound",
  cancelKey = "common.cancel",
}) => {
  const { t, i18n } = useTranslation(ns);
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { bodyCopy, backgroundBtn } = colorConfig;
  const locale = i18n.language || "en";

  const [query, setQuery] = useState("");

  const options = useMemo(
    () =>
      countries
        .filter((c) => c.effectiveStatus === "AVAILABLE")
        .map((c) => ({
          ...c,
          countryName: countryDisplayName(c.countryCode, locale) || c.countryName,
        }))
        .slice()
        .sort((a, b) => a.countryName.localeCompare(b.countryName)),
    [countries, locale]
  );

  const normalize = (value: string) =>
    value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const filtered = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return options;
    return options.filter((c) =>
      [
        c.countryName,
        c.countryCode,
        trailingLabel?.(c) ?? currencyForCountry(c) ?? "",
      ].some((field) => normalize(field).includes(q))
    );
  }, [options, query, trailingLabel]);

  const handleSelect = (countryCode: string) => {
    onSelect(countryCode);
    onClose();
  };

  return (
    <section className="select-payout-slot">
      <header className="payout-details-header">
        <span className="payout-header-action" aria-hidden="true" />
        <Title title={t(titleKey)} align="center" color={bodyCopy} />
        <button
          type="button"
          className="payout-header-action payout-close-btn"
          aria-label={t(cancelKey)}
          onClick={onClose}
        >
          <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
      </header>
      <input
        type="text"
        className="select-country-search"
        placeholder={t(searchKey)}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label={t(searchKey)}
        autoComplete="off"
      />
      <ul className="select-payout-list">
        <ScrollFadeIndicator color={bodyCopy} />
        {filtered.length === 0 ? (
          <li className="select-country-empty">
            <GradientText textColor={bodyCopy}>
              {t(emptyKey)}
            </GradientText>
          </li>
        ) : null}
        {filtered.map((country) => {
          const isSelected = selected === country.countryCode;
          return (
            <li
              key={country.countryCode}
              className={`select-payout-option ${
                isSelected ? "selected" : ""
              }`.trim()}
              onClick={() => handleSelect(country.countryCode)}
              style={
                {
                  "--bgc": backgroundBtn,
                } as React.CSSProperties
              }
            >
              <section className="select-payout-icon">
                <FlagCircle iso2={country.countryCode.toLowerCase()} size={24} />
              </section>
              <GradientText
                textColor={bodyCopy}
                className="select-payout-label"
              >
                {country.countryName}
              </GradientText>
              {(trailingLabel?.(country) ?? currencyForCountry(country)) ? (
                <GradientText
                  textColor={bodyCopy}
                  className="select-payout-currency"
                >
                  {trailingLabel?.(country) ?? currencyForCountry(country)}
                </GradientText>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
};
